import dayjs from "dayjs"
import BaseInputField from "../../../../components/elements/BaseInputField"
import BaseModalPage from "../../../../components/elements/BaseModalPage"
import { ConfigDayType } from "../../types"
import { CostCenterType, Employee } from "../../../Mitarbeiter/types/Types"
import BaseCheckbox from "../../../../components/elements/BaseCheckbox"
import { transformSecondsToMinutes } from "../../../../services/functions"
import { transformMinutesToSeconds } from "../../../../services/functions"
import { ReactComponent as CHEVRON_DOWN } from "../../../../assets/ChevronDown.svg"
import { ReactComponent as CHEVRON_UP } from "../../../../assets/ChevronUp.svg"
import { useEffect, useState } from "react"
type RouteOptimizationModalScreen1Type = {
    visible: boolean
    startDate: Date
    globalStartTime: string
    setGlobalStartTime: (value: string) => void
    globalEndTime: string
    setGlobalEndTime: (value: string) => void
    selectedDays: { [key: string]: ConfigDayType }
    handleChangeDayConfig: (day: any, attribute: keyof ConfigDayType, value: any) => void
    employee: Employee
}

export default function RouteOptimizationModalScreen1(props: RouteOptimizationModalScreen1Type) {
    const [blockedDays, setBlockedDays] = useState<string[]>([])

    useEffect(() => {
        const daysOfWeek = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"]
        function getWeekdaysUntilToday(currentDateStr: Date) {
            const currentDate = new Date(currentDateStr)

            let dayIndex = currentDate.getDay()
            if (dayIndex === 0) dayIndex = 7

            return daysOfWeek.slice(0, dayIndex)
        }

        if (dayjs(props.startDate).week() === dayjs().week() && blockedDays && blockedDays?.length !== 0) {
            setBlockedDays(getWeekdaysUntilToday(new Date()))
        } else if (dayjs(props.startDate).week() < dayjs().week() && blockedDays && blockedDays?.length !== 0) {
            setBlockedDays(daysOfWeek)
        } else if (dayjs(props.startDate).week() > dayjs().week() && blockedDays && blockedDays?.length !== 0) {
            setBlockedDays([])
        }
    }, [blockedDays, props.startDate])

    return (
        <BaseModalPage visible={props.visible}>
            <div className="flex flex-row gap-4 pt-2">
                <BaseInputField label="Startzeit" type="time" value={props.globalStartTime} onChange={props.setGlobalStartTime} />
                <BaseInputField label="Endzeit" type="time" value={props.globalEndTime} onChange={props.setGlobalEndTime} />
            </div>
            {Object.keys(props.selectedDays).map((day) => (
                <ConfigDayElement
                    key={day}
                    keyLabel={day}
                    blocked={blockedDays.includes(day)}
                    item={props.selectedDays[day as keyof typeof props.selectedDays] as ConfigDayType}
                    onChange={props.handleChangeDayConfig}
                    kostenstelle={props.employee?.kostenstelle as CostCenterType[]}
                    employee={props.employee}
                />
            ))}
        </BaseModalPage>
    )
}

type ConfigDayElementProps = {
    item: ConfigDayType
    keyLabel: string
    onChange: (day: any, attribute: keyof ConfigDayType, value: any) => void
    kostenstelle: CostCenterType[]
    employee: Employee
    blocked: boolean
}

function ConfigDayElement(props: ConfigDayElementProps) {
    const [showConfig, setShowConfig] = useState(false)
    return (
        <div
            className={`flex flex-col items-start pt-2 border-2 border-black rounded-default p-4 mt-2 ${
                (!props.item.enabled || props.blocked) && "opacity-60 bg-gray-100"
            }`}>
            <div className="flex flex-row justify-between items-center w-full ">
                <BaseCheckbox
                    disabled={props.blocked}
                    label={
                        props.item.label +
                        (props.blocked
                            ? " (Dieser Tag liegt in der Vergangenheit)"
                            : props.item.enabled
                            ? ""
                            : " (Für diesen Tag wird keine Route geplant)")
                    }
                    check={props.blocked ? false : props.item.enabled}
                    setcheck={() => props.onChange(props.keyLabel, "enabled", !props.item.enabled)}
                />

                {props.item.enabled && !props.blocked ? (
                    !showConfig ? (
                        <CHEVRON_DOWN className="w-6 h-6 mt-2 cursor-pointer" onClick={() => setShowConfig(true)} />
                    ) : (
                        <CHEVRON_UP className="w-6 h-6 mt-2 cursor-pointer" onClick={() => setShowConfig(false)} />
                    )
                ) : (
                    <></>
                )}
            </div>
            {props.item.enabled && showConfig && (
                <>
                    <p className="text-sm font-bold pt-4 border-b pb-2 w-full mb-2">Zeiten</p>
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 w-full">
                        <BaseInputField
                            label="Startzeit"
                            type="time"
                            value={props.item.startTime}
                            onChange={(e: any) => props.onChange(props.keyLabel, "startTime", e)}
                        />
                        <BaseInputField
                            label="Endzeit"
                            type="time"
                            value={props.item.endTime}
                            onChange={(e: any) => props.onChange(props.keyLabel, "endTime", e)}
                        />
                        <BaseInputField
                            label="Toleranz in Minuten"
                            type="number"
                            value={transformSecondsToMinutes(props.item.timeFrameToleranceInSeconds ?? 0)}
                            onChange={(e: any) => props.onChange(props.keyLabel, "timeFrameToleranceInSeconds", transformMinutesToSeconds(Number(e)))}
                        />
                    </div>
                    <p className="text-sm font-bold pt-4 border-b pb-2 w-full mb-2">Ausgangsadresse</p>
                    <div className="flex flex-col md:flex-row md:gap-4 w-full justify-evenly">
                        {props.kostenstelle?.map((kostenstelle) => (
                            <BaseCheckbox
                                check={props.item.startAddress === kostenstelle?.address}
                                label={"Niederlassung " + kostenstelle?.name}
                                setcheck={() => props.onChange(props.keyLabel, "startAddress", kostenstelle?.address?.coordinates)}
                            />
                        ))}
                        <BaseCheckbox
                            check={props.item.startAddress === props.employee?.address}
                            label={"Adresse des Mitarbeiters"}
                            setcheck={() => props.onChange(props.keyLabel, "startAddress", props.employee?.address?.coordinates)}
                        />
                    </div>
                    <p className="text-sm font-bold pt-4 border-b pb-2 w-full mb-2">Endadresse</p>
                    <div className="flex flex-col md:flex-row md:gap-4 w-full justify-evenly">
                        {props.kostenstelle?.map((kostenstelle) => (
                            <BaseCheckbox
                                check={props.item.endAddress === kostenstelle?.address}
                                label={"Niederlassung " + kostenstelle?.name}
                                setcheck={() => props.onChange(props.keyLabel, "endAddress", kostenstelle?.address?.coordinates)}
                            />
                        ))}
                        <BaseCheckbox
                            check={props.item.endAddress === props.employee?.address}
                            label={"Adresse des Mitarbeiters"}
                            setcheck={() => props.onChange(props.keyLabel, "endAddress", props.employee?.address?.coordinates)}
                        />
                    </div>
                </>
            )}
        </div>
    )
}
